import type { DynamicFeeBracket } from "@investhinks/api-client";
import React from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Input, InputGroup, InputGroupAddon, InputGroupText, Table } from "reactstrap";

interface DynamicFeeEditorProps {
  value?: DynamicFeeBracket[];
  onChange: (tiers: DynamicFeeBracket[]) => void;
  disabled?: boolean;
}

/**
 * DynamicFeeEditor component for editing balance-based profit fee brackets.
 *
 * Expected value format: Array<{ below?: number | null; fee: number }>
 * - below: Balance threshold (null = catch-all for highest balances)
 * - fee: Profit fee as decimal (0.25 = 25%)
 *
 * Example:
 * [
 *   { below: 5000, fee: 0.40 },   // Balance < 5000 -> 40% fee
 *   { below: 100000, fee: 0.25 }, // Balance < 100000 -> 25% fee
 *   { below: null, fee: 0.23 },   // Balance >= 100000 -> 23% fee (catch-all)
 * ]
 */
const DynamicFeeEditor: React.FC<DynamicFeeEditorProps> = ({ value = [], onChange, disabled = false }) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "settings.dynamicFee" });

  // Ensure we always have at least the catch-all tier
  const tiers = Array.isArray(value) && value.length > 0 ? value : [{ below: null, fee: 0.25 }];

  // Sort tiers: numbered thresholds ascending, null (catch-all) last
  const sortTiers = (items: DynamicFeeBracket[]): DynamicFeeBracket[] => {
    return [...items].sort((a, b) => {
      if (a.below === null) return 1;
      if (b.below === null) return -1;
      return (a.below || 0) - (b.below || 0);
    });
  };

  // Regex to validate numeric input (integers and decimals)
  const isValidNumber = (val: string): boolean => /^[0-9]*\.?[0-9]*$/.test(val);

  const handleTierChange = (index: number, field: "below" | "fee", newValue: string) => {
    // Validate input is numeric
    if (newValue !== "" && !isValidNumber(newValue)) {
      return;
    }

    const updatedTiers = [...tiers];

    if (field === "below") {
      updatedTiers[index] = {
        ...updatedTiers[index],
        below: newValue === "" || newValue === null ? null : parseFloat(newValue),
      };
    } else if (field === "fee") {
      // Convert from percentage (e.g., "25") to decimal (0.25)
      const feeValue = newValue === "" ? 0 : parseFloat(newValue) / 100;
      updatedTiers[index] = {
        ...updatedTiers[index],
        fee: Math.max(0, Math.min(1, feeValue)),
      };
    }

    onChange(sortTiers(updatedTiers));
  };

  const handleAddTier = () => {
    // Find a good default threshold (highest existing threshold + 5000)
    const existingThresholds = tiers.filter((tier) => tier.below !== null).map((tier) => tier.below || 0);
    const maxThreshold = existingThresholds.length > 0 ? Math.max(...existingThresholds) : 0;
    const newThreshold = maxThreshold + 5000;

    // Default to 20% fee for new tier
    const newTiers = [...tiers, { below: newThreshold, fee: 0.2 }];
    onChange(sortTiers(newTiers));
  };

  const handleRemoveTier = (index: number) => {
    if (tiers.length <= 1) return;
    const updatedTiers = tiers.filter((_, i) => i !== index);
    onChange(sortTiers(updatedTiers));
  };

  // Validation
  const hasCatchAll = tiers.some((tier) => tier.below === null);
  const hasNoDuplicates = new Set(tiers.map((tier) => tier.below)).size === tiers.length;

  const sortedTiers = sortTiers(tiers);

  return (
    <div className="dynamic-fee-editor">
      {!hasCatchAll && (
        <Alert color="warning" className="mb-2 py-2">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          {t("noCatchAllWarning")}
        </Alert>
      )}

      {!hasNoDuplicates && (
        <Alert color="warning" className="mb-2 py-2">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          {t("duplicateThresholdWarning")}
        </Alert>
      )}

      <Table size="sm" bordered className="mb-2" responsive>
        <thead className="thead-light">
          <tr>
            <th style={{ width: "45%" }}>{t("columnBelow")}</th>
            <th style={{ width: "35%" }}>{t("columnFee")}</th>
            <th style={{ width: "20%", textAlign: "center" }}>{t("columnActions")}</th>
          </tr>
        </thead>
        <tbody>
          {sortedTiers.map((tier, index) => {
            const isCatchAll = tier.below === null;
            const feePercent = (tier.fee * 100).toFixed(0);

            return (
              <tr key={index}>
                <td className="align-middle">
                  {isCatchAll ? (
                    <div className="d-flex align-items-center" style={{ height: "31px" }}>
                      <span className="text-muted font-italic">
                        <i className="fas fa-infinity mr-2"></i>
                        {t("noLimit")}
                      </span>
                    </div>
                  ) : (
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>&lt;</InputGroupText>
                      </InputGroupAddon>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={tier.below || ""}
                        onChange={(e) => handleTierChange(index, "below", e.target.value)}
                        disabled={disabled}
                        style={{ maxWidth: "100px" }}
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText>EUR</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  )}
                </td>
                <td className="align-middle">
                  <InputGroup>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={feePercent}
                      onChange={(e) => handleTierChange(index, "fee", e.target.value)}
                      disabled={disabled}
                      style={{ maxWidth: "70px" }}
                      className={tier.fee < 0 || tier.fee > 1 ? "is-invalid" : ""}
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText>%</InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                </td>
                <td className="text-center align-middle" style={{ verticalAlign: "middle" }}>
                  {!isCatchAll && tiers.length > 1 && (
                    <Button
                      color="link"
                      size="sm"
                      className="text-danger p-1"
                      onClick={() => handleRemoveTier(index)}
                      disabled={disabled}
                      title={t("removeTier")}
                      style={{ lineHeight: 1 }}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </Button>
                  )}
                  {isCatchAll && (
                    <span className="text-muted" title={t("catchAllRequired")} style={{ fontSize: "0.875rem" }}>
                      <i className="fas fa-lock"></i>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Button color="secondary" size="sm" outline onClick={handleAddTier} disabled={disabled}>
        <i className="fas fa-plus mr-2"></i>
        {t("addTier")}
      </Button>

      <div className="mt-2">
        <small className="text-muted">{t("helpText")}</small>
      </div>
    </div>
  );
};

export default DynamicFeeEditor;
